import React, { Fragment, lazy } from 'react';
import { observer } from 'mobx-react';

import { BILLING_DASHBOARD_PAGE } from '@/billing/constants/pages';
import Link, { NONE } from '@/common/components/Link/Link';
import Separator from '@/common/components/Separator/Separator';
import { ClientSetting } from '@/common/constants/clientSettings';
import { CATALOG_GEMS_PAGE } from '@/common/constants/pages';
import { getPricingUrl } from '@/common/helpers/getPricingUrl';
import { MANAGER, securityManager } from '@/common/services/SecurityManager';
import URLProvider from '@/common/services/URLProvider';
import { useUserStore } from '@/user/stores/UserStore';

import headerStyles from '../Header.module.css';

const Drawer = lazy(() => import('../../Drawer/Drawer'));

export interface MobileDrawerProps {
  isOpened: boolean;
  closeMobileMenu: () => void;
  onCollectionClick: () => void;
  onPresentationClick: () => void;
  onWorkshopClick: () => void;
  onPricingClick: () => void;
  onPrivacyPolicyClick: () => void;
  onLogout: () => void;
  redirectToLoginPage: (e: React.MouseEvent) => void;
}

function MobileDrawer(props: MobileDrawerProps) {
  const {
    isOpened,
    closeMobileMenu,
    onCollectionClick,
    onPresentationClick,
    onWorkshopClick,
    onPricingClick,
    onPrivacyPolicyClick,
    onLogout,
    redirectToLoginPage,
  } = props;
  const { user, isAuthorized } = useUserStore();

  return (
    <Drawer isOpened={isOpened} onClose={closeMobileMenu}>
      <div className={headerStyles.headerMobileItemList}>
        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Natural Colorless Diamonds"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/diamond-colorless"
          >
            Natural Colorless Diamonds
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Natural Fancy Color Diamonds"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/diamond-fancy"
          >
            Natural Fancy Color Diamonds
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Natural Rough"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/rough"
          >
            Natural Rough
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Lab-Grown Colorless Diamonds"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/lgd-colorless"
          >
            Lab-Grown Colorless Diamonds
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Lab-Grown Fancy Color Diamonds"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/lgd-fancy"
          >
            Lab-Grown Fancy Color Diamonds
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Lab-Grown Rough"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/lgd-rough"
          >
            Lab-Grown Rough
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Jewelry"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to="/catalog/jewelry"
          >
            Jewelry
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Gems"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            exact
            onClick={closeMobileMenu}
            to={CATALOG_GEMS_PAGE}
          >
            Gems
          </Link>
        </div>
      </div>

      <Separator />

      <div className={headerStyles.headerMobileItemList}>
        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Demo"
            activeClassName={headerStyles.active}
            appearance={NONE}
            className={headerStyles.headerNavLink}
            onClick={onCollectionClick}
            to="/collections"
          >
            Demo
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Technology Overview"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="/pr/#/"
            onClick={onPresentationClick}
          >
            Technology Overview
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="What's new"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="https://octonus-teams.com/wiki/display/CUDO/Release+Notes"
          >
            What&apos;s new
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Embed Cutwise Widget"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="https://octonus-teams.com/wiki/display/CUDO/Cutwise+Widget"
          >
            Embed Cutwise Widget
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link aria-label="API Documentation" appearance={NONE} className={headerStyles.headerNavLink} external href="https://api-doc.cutwise.com/">
            API Documentation
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="YouTube Channel"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="https://www.youtube.com/channel/UCgIrQJ25wIuLfCPbFcZ4S6Q"
          >
            YouTube Channel
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Performance Workshop"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="https://docs.cutwise.com/knowledge-base/performance-workshop"
            onClick={onWorkshopClick}
          >
            Performance Workshop
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Pricing"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href={getPricingUrl()}
            onClick={onPricingClick}
          >
            Pricing
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link
            aria-label="Privacy Policy"
            appearance={NONE}
            className={headerStyles.headerNavLink}
            external
            href="/privacy"
            onClick={onPrivacyPolicyClick}
          >
            Privacy Policy
          </Link>
        </div>

        <div className={headerStyles.headerMobileItem}>
          <Link aria-label="Contacts" appearance={NONE} className={headerStyles.headerNavLink} external href="/about">
            Contacts
          </Link>
        </div>
      </div>

      <div className={headerStyles.headerMobileItemList}>
        {isAuthorized ? (
          <Fragment>
            <div className={headerStyles.headerMobileItem}>
              <Link
                aria-label="My Profile"
                appearance={NONE}
                className={headerStyles.headerNavLink}
                to={URLProvider.profile(user!)}
                onClick={closeMobileMenu}
              >
                My Profile
              </Link>
            </div>

            {!user?.hasSetting(ClientSetting.CustomBilling) && securityManager.isGranted(MANAGER) && (
              <div className={headerStyles.headerMobileItem}>
                <Link
                  aria-label="Billing Dashboard"
                  appearance={NONE}
                  className={headerStyles.headerNavLink}
                  to={BILLING_DASHBOARD_PAGE}
                  onClick={closeMobileMenu}
                >
                  Billing Dashboard
                </Link>
              </div>
            )}

            <div className={headerStyles.headerMobileItem}>
              <Link aria-label="Log out" appearance={NONE} className={headerStyles.headerNavLink} href="#" onClick={onLogout}>
                Log out
              </Link>
            </div>
          </Fragment>
        ) : (
          <div className={headerStyles.headerMobileItem}>
            <Link aria-label="Log in" appearance={NONE} className={headerStyles.headerNavLink} href="#" onClick={redirectToLoginPage}>
              Log in
            </Link>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default observer(MobileDrawer);
